"use client";

/**
 * fund-drawdown-card.tsx
 *
 * Three-metric card showing the fund's worst historical drawdown:
 * max fall %, ₹1L impact at trough, and recovery time. Includes the
 * peak → trough → recovered timeline below.
 *
 * Used in: src/app/funds/[slug]/page.tsx (Risk Analysis section — PRO tier)
 */

import { formatRupees, formatPctDirect } from "@/lib/utils/format";

export interface FundDrawdownCardProps {
  maxDrawdownPct: number | null; // already as signed percent e.g. -32.4
  maxDrawdownLakhValue: number | null;
  peakDate: string | null;
  troughDate: string | null;
  recoveryMonths: number | null;
}

function formatMonthYear(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

export function FundDrawdownCard({
  maxDrawdownPct,
  maxDrawdownLakhValue,
  peakDate,
  troughDate,
  recoveryMonths,
}: FundDrawdownCardProps) {
  if (maxDrawdownPct === null || maxDrawdownPct === undefined) {
    return (
      <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
        <p className="text-muted-foreground text-sm">
          Insufficient NAV history for drawdown calculation.
        </p>
      </div>
    );
  }

  const recoveryText =
    recoveryMonths === null
      ? "Not recovered"
      : recoveryMonths < 1
      ? "< 1 month"
      : `${recoveryMonths} month${recoveryMonths === 1 ? "" : "s"}`;

  return (
    <div
      className="rounded-xl p-5 border border-cred/10"
      style={{ backgroundColor: "rgba(239,68,68,0.06)" }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Worst fall */}
        <div>
          <div className="text-muted-foreground text-[11px] uppercase tracking-wide mb-1">
            Worst fall
          </div>
          <div className="text-cred text-[22px] font-bold font-tabular leading-none">
            {formatPctDirect(maxDrawdownPct)}
          </div>
        </div>

        {/* ₹1L became */}
        <div>
          <div className="text-muted-foreground text-[11px] uppercase tracking-wide mb-1">
            ₹1L became
          </div>
          <div className="text-cred text-[22px] font-bold font-tabular leading-none">
            {formatRupees(maxDrawdownLakhValue)}
          </div>
        </div>

        {/* Recovery */}
        <div>
          <div className="text-muted-foreground text-[11px] uppercase tracking-wide mb-1">
            Recovery
          </div>
          <div className="text-cgreen text-[22px] font-bold font-tabular leading-none">
            {recoveryText}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-4 text-muted-foreground text-xs">
        Peak:{" "}
        <span className="text-foreground">{formatMonthYear(peakDate)}</span>
        {" → Trough: "}
        <span className="text-foreground">{formatMonthYear(troughDate)}</span>
        {recoveryMonths !== null && (
          <>
            {" → Recovered in "}
            <span className="text-cgreen">{recoveryText}</span>
          </>
        )}
      </div>
    </div>
  );
}
